import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { apiError, brl, SPORT_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const BADGE = { REQUESTED: "bg-amber-500/15 text-amber-300 border-amber-500/30", APPROVED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", REJECTED: "bg-red-500/15 text-red-300 border-red-500/30" };
const LABEL = { REQUESTED: "Em análise", APPROVED: "Aprovado", REJECTED: "Recusado" };

export default function Refunds() {
  const { user } = useAuth();
  const canDecide = user?.role === "SUPER_ADMIN" || (user?.permissions || []).includes("FINANCE");
  const [list, setList] = useState([]);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/staff/refunds").then((r) => setList(r.data.refunds)).catch(() => {});
  useEffect(() => { load(); }, []);

  const decide = async (reg, approve) => {
    setBusy(reg.id);
    try {
      await api.patch(`/registrations/${reg.id}/refund`, { approve, note: notes[reg.id] || "" });
      toast.success(approve ? "Reembolso aprovado e inscrição cancelada" : "Solicitação recusada");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const pending = list.filter((r) => r.refund.status === "REQUESTED").length;

  return (
    <div className="space-y-6" data-testid="refunds-page">
      <div>
        <p className="text-brand font-bold uppercase tracking-widest text-xs">Financeiro</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase">Cancelamentos e Reembolsos</h1>
        <p className="text-slate-400 text-sm mt-1"><b className="text-amber-300" data-testid="refunds-pending">{pending}</b> pendente(s) • prazo legal de 7 dias (Art. 5º)</p>
      </div>
      <div className="space-y-3">
        {list.length === 0 && <p className="text-slate-500 text-sm">Nenhuma solicitação.</p>}
        {list.map((reg) => (
          <div key={reg.id} data-testid={`refund-row-${reg.bib_code}`} className="rounded-2xl bg-surface border border-white/10 p-4 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-neon tracking-widest">{reg.bib_code}</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${BADGE[reg.refund.status]}`}>{LABEL[reg.refund.status]}</span>
                {!reg.refund.refundable && <span className="text-[11px] text-slate-500">sem pagamento</span>}
              </div>
              <div className="font-semibold truncate">{reg.runner?.full_name} <span className="text-slate-500 font-normal">• {reg.runner?.email || "sem e-mail"}</span></div>
              <div className="text-xs text-slate-400">{SPORT_LABEL[reg.category?.sport_type]} — {reg.category?.name} • {brl(reg.category?.price)} • pedido em {new Date(reg.refund.requested_at).toLocaleString("pt-BR")}</div>
              {reg.refund.reason && <div className="text-xs text-slate-300 mt-1 italic">"{reg.refund.reason}"</div>}
              {reg.refund.decided_by && <div className="text-xs text-slate-500 mt-1">Decidido por {reg.refund.decided_by} em {new Date(reg.refund.decided_at).toLocaleString("pt-BR")}{reg.refund.note ? ` — ${reg.refund.note}` : ""}</div>}
            </div>
            {canDecide && reg.refund.status === "REQUESTED" && (
              <div className="flex gap-2 items-center">
                <Input data-testid={`refund-note-${reg.bib_code}`} placeholder="Observação (opcional)" value={notes[reg.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [reg.id]: e.target.value }))} className="bg-ink border-white/10 w-48" />
                <Button data-testid={`refund-approve-${reg.bib_code}`} onClick={() => decide(reg, true)} disabled={busy === reg.id} className="bg-brand hover:bg-brand-hover font-bold">{busy === reg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Aprovar</>}</Button>
                <Button data-testid={`refund-reject-${reg.bib_code}`} onClick={() => decide(reg, false)} disabled={busy === reg.id} variant="ghost" className="border border-red-500/30 text-red-300 hover:bg-red-500/10 font-bold"><X className="w-4 h-4 mr-1" /> Recusar</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <Button variant="ghost" onClick={load} className="border border-white/10 text-xs"><RotateCcw className="w-3 h-3 mr-2" /> Atualizar</Button>
    </div>
  );
}
